import React, { useState, useEffect, useRef } from "react";
import { ControlsDropdown } from "../base/ControlsDropdown";
import { PanelDropdown } from "../base/PanelDropdown";
import { useExtendedMutation, runAllMutationsWithArgs } from "mutations";
import { getPrivateCategories, getPrivateSubcategories } from "api";
import "./PrivateCategoriesSettingsControls.css";

const PrivateCategoriesSettingsControls = ({ token, onSelectionChange, currentCategoryId, currentSubcategoryId, onLoaded, onLoadingChange, variant = 'default' }) => {
  const Dropdown = variant === 'secondary' ? PanelDropdown : ControlsDropdown;
  const [privateCategories, setPrivateCategories] = useState([]);
  const [privateSubcategories, setPrivateSubcategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState('');
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [subcategoriesLoaded, setSubcategoriesLoaded] = useState(false);
  const categoryChangedInternally = useRef(false);

  useEffect(() => {
    if (categoriesLoaded && subcategoriesLoaded) onLoaded?.();
  }, [categoriesLoaded, subcategoriesLoaded]);

  const errorCallback = (error) => {
    console.log(error);
    onLoadingChange?.(false);
  }

  const getPrivateCategoriesMutation = useExtendedMutation(
    getPrivateCategories,
    (data) => { setPrivateCategories(data); setCategoriesLoaded(true); },
    errorCallback
  );

  const getPrivateSubcategoriesMutation = useExtendedMutation(
    getPrivateSubcategories,
    (data) => { setPrivateSubcategories(data); setSubcategoriesLoaded(true); onLoadingChange?.(false); },
    errorCallback
  );

  useEffect(() => {
    runAllMutationsWithArgs([
      { mutation: getPrivateCategoriesMutation, input: { token } },
      { mutation: getPrivateSubcategoriesMutation, input: { token, categoryId: currentCategoryId } },
    ]);
  }, [token]);

  useEffect(() => {
    if (currentCategoryId) {
      setSelectedCategoryId(currentCategoryId);
      if (!categoryChangedInternally.current) {
        onLoadingChange?.(true);
        getPrivateSubcategoriesMutation.mutateOnVirtualThread({ token, categoryId: currentCategoryId });
      }
      categoryChangedInternally.current = false;
    }
    if (currentSubcategoryId) setSelectedSubcategoryId(currentSubcategoryId);
  }, [currentCategoryId, currentSubcategoryId]);

  const categoryOptions = privateCategories.map(c => ({ value: c.categoryId, label: c.title }));
  const subcategoryOptions = privateSubcategories.map(s => ({ value: s.subcategoryId, label: s.title }));

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    categoryChangedInternally.current = true;
    setSelectedCategoryId(categoryId);
    setSelectedSubcategoryId('');
    onSelectionChange({ categoryId, subcategoryId: null });
    onLoadingChange?.(true);
    getPrivateSubcategoriesMutation.mutateOnVirtualThread({ token, categoryId });
  };

  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    setSelectedSubcategoryId(subcategoryId);
    onSelectionChange({ categoryId: null, subcategoryId });
  };

  const onlyOneCategory = categoryOptions.length <= 1;
  const onlyOneSubcategory = subcategoryOptions.length <= 1;

  return (
    <div className="controls-inline-row">
      <div>
        <Dropdown
          label={"Choose Category"}
          options={categoryOptions}
          onChange={handleCategoryChange}
          value={selectedCategoryId}
          disabled={onlyOneCategory}
          disabledTooltip={"Only one category available"}
        />
      </div>
      <div>
        <Dropdown
          label={"Choose Subcategory"}
          options={subcategoryOptions}
          onChange={handleSubcategoryChange}
          value={selectedSubcategoryId}
          disabled={onlyOneSubcategory}
          disabledTooltip={"Only one subcategory available"}
        />
      </div>
    </div>
  );
}

export { PrivateCategoriesSettingsControls }
